import { UpsellResponseKey } from '@leanpay/models'
import { LEANPAY_RO_SUPPORT } from '@leanpay/ui'
import { Environment, getDefaultAppLanguage, Market } from '@leanpay/utils'

const getMarketFromHostname = (): Market => {
    const hostname = window.location.hostname

    if (hostname.includes('.leanpay.ro') || hostname.includes('rou')) {
        return Market.ROM
    }

    if (hostname.includes('.leanpay.si') || hostname.includes('slo')) {
        return Market.SLO
    }

    throw new Error(`[MARKET] Unknown hostname: ${hostname}`)
}

const isPrivateIp =
    /^(10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(
        window.location.hostname,
    )
const isRunningOnLocalhost =
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    isPrivateIp ||
    (window.location.hostname.includes('leanpay') && !window.location.hostname.includes('.'))

export const currentMarket = isRunningOnLocalhost
    ? (import.meta.env.VITE_MARKET as Market)
    : getMarketFromHostname()

export const isMarketSlo = currentMarket === Market.SLO
export const isMarketRom = currentMarket === Market.ROM

const getEnvironmentFromHostname = () => {
    const hostname = window.location.hostname

    if (isRunningOnLocalhost) {
        return Environment.LOCAL
    }

    if (hostname.includes('dev')) {
        return Environment.TILT
    }

    if (hostname.includes('test-credit')) {
        return Environment.TEST
    }
    if (hostname.includes('stage-credit')) {
        return Environment.STAGE
    }

    if (hostname.includes('credit')) {
        return Environment.PROD
    }

    throw new Error(`[ENV] Unknown hostname: ${hostname}`)
}

export const currentEnvironment = getEnvironmentFromHostname()

export const isLocalEnvironment = currentEnvironment === Environment.LOCAL
export const isTestEnvironment = currentEnvironment === Environment.TEST
export const isStageEnvironment = currentEnvironment === Environment.STAGE
export const isProdEnvironment = currentEnvironment === Environment.PROD
export const isNonProdLiveEnvironment = !isProdEnvironment && !isLocalEnvironment

export const defaultAppLanguage = getDefaultAppLanguage(currentMarket)

export enum CreditRoute {
    RequestOtp = 'request-otp',
    Login = 'login',
    Consents = 'consents',
    ValidateOffer = 'validate-offer',
    OfferPlanData = 'offer-plan-data',
    EnterIban = 'enter-iban',
    ScoringDecision = 'scoring-decision',
    OfferConfirmation = 'offer-confirmation',
    SignContract = 'sign-contract',
    PepQuestionnaire = 'pep-questionnaire',
    CreditSuccess = 'credit-success',
    CreditError = 'credit-error',
    CreditRejection = 'credit-rejection',
    ForgotTwoFaPin = 'forgot-two-fa-pin',
    TwoFaPin = 'two-fa-pin',
    EnterAmount = 'enter-amount',
    ValidationCheck = 'validation-check',
    UpsellBlocked = 'upsell-blocked',
}

export const UPSELL_FLOW = 'upsell'

// The same screen that each market's OTP login goes to.
export const HANDOFF_LANDING_ROUTE = isMarketSlo
    ? CreditRoute.ValidationCheck
    : CreditRoute.Consents
export const ACCESS_DENIED_ERROR_STATE = { error: 'DATA_NOT_VALID' } as const
// Tokenless GPL marketing entry (nginx routes /info to this SPA on both markets).
export const MARKETING_ENTRY_PATH = '/info'
export enum UpsellBlockedVariant {
    PersonalizedOffer = 'PERSONALIZED_OFFER',
    OfferUnavailable = 'OFFER_UNAVAILABLE',
}

// checkout-init 400 response keys → blocked-screen variant (strings pending BE finalization, OQ-01).
// Recipient mismatch is NOT here — it surfaces post-login as SM state RECIPIENT_CHECK_FAILURE
// (handled in validation-check-slo), never as a checkout-init response key.
export const UPSELL_RESPONSE_KEY_TO_VARIANT: Record<string, UpsellBlockedVariant> = {
    [UpsellResponseKey.UPSELL_OFFER_NO_LONGER_AVAILABLE]: UpsellBlockedVariant.OfferUnavailable,
    [UpsellResponseKey.UPSELL_OFFER_EXPIRED]: UpsellBlockedVariant.OfferUnavailable,
    [UpsellResponseKey.UPSELL_NOT_ELIGIBLE]: UpsellBlockedVariant.OfferUnavailable,
}

export const RO_FAQ_URL = LEANPAY_RO_SUPPORT.faqUrl
export const LEANPAY_SI_URL = 'https://www.leanpay.si'
export const LEANPAY_RO_URL = 'https://www.leanpay.ro'
export const SLO_CASH_LOAN_FAQ_URL =
    'https://help.leanpay.com/sl/knowledge/leanpay-gotovinski-kredit'

export const redirectToFaq = () => {
    window.location.href = RO_FAQ_URL
}

export const getDashboardUrl = (): string => {
    switch (currentMarket) {
        case Market.HUN:
            if (isProdEnvironment) {
                return 'https://app.leanpay.hu/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.hu/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.hu/dashboard'
            }
            return 'http://localhost:4200/dashboard'
        case Market.ROM:
            if (isProdEnvironment) {
                return 'https://app.leanpay.ro/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.ro/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.ro/dashboard'
            }

            return 'http://localhost:4200/dashboard'
        case Market.SLO:
            if (isProdEnvironment) {
                return 'https://app.leanpay.si/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.si/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.si/dashboard'
            }
            return 'http://localhost:4200/dashboard'
        default:
            return 'http://localhost:4200/dashboard'
    }
}

export const redirectToDashboard = () => {
    window.location.href = getDashboardUrl()
}

// SLO GPL failure reasons carried from the loader to /credit-error
// (mirror legacy mpl-add.updatePaymentPlansAndExtractTermOptions / validateOffer).
export enum CreditFailureReason {
    FailCreditCheck = 'FAIL_CREDIT_CHECK',
    NoPaymentOptions = 'NO_PAYMENT_OPTIONS',
    ScoringFailed = 'SCORING_FAILED',
    // SISBON out of working hours — post-sign checkout-status poll business error.
    OnDutyUserNotLoggedIn = 'ON_DUTY_USER_NOT_LOGGED_IN',
    Generic = 'GENERIC',
}

export enum CreditErrorVariant {
    GENERIC = 'GENERIC',
    REJECTION = 'REJECTION',
    ANAF_UNAVAILABLE = 'ANAF_UNAVAILABLE',
    CB_UNAVAILABLE = 'CB_UNAVAILABLE',
    ACCESS_DENIED = 'ACCESS_DENIED',
    NO_POSSIBLE_PLANS = 'NO_POSSIBLE_PLANS',
    NO_PAYMENT_OPTIONS = 'NO_PAYMENT_OPTIONS',
    // Value matches the API error code so resolveVariant maps it automatically.
    WRONG_TOKEN = 'USER_AUTHENTICATION_WRONG_TOKEN',
}

export enum CacheKey {
    SessionInfo = 'SessionInfo',
    VendorToken = 'VendorToken',
    StateMachineStatus = 'StateMachineStatus',
    LoanAmountLimits = 'LoanAmountLimits',
    PaymentData = 'PaymentData',
    MplPaymentData = 'MplPaymentData',
    CreditTokenValidation = 'CreditTokenValidation',
    ContractData = 'ContractData',
    PepQuestionnaire = 'PepQuestionnaire',
    PepQuestionnaireSlo = 'PepQuestionnaireSlo',
    PurchaseLimit = 'PurchaseLimit',
    MplPurchaseLimit = 'MplPurchaseLimit',
    EligibilityValidation = 'EligibilityValidation',
    PlanCalculation = 'PlanCalculation',
    OfferValidation = 'OfferValidation',
    PepStatus = 'PepStatus',
    CheckoutStateMachineStatus = 'CheckoutStateMachineStatus',
    IbanData = 'IbanData',
    FinancialDataStatus = 'FinancialDataStatus',
    MplCheckoutStatus = 'MplCheckoutStatus',
}
